export interface SalesReturnUnitItem {
  id?: number | string;
  original_order_item_id?: number | string | null;
  item_type?: string;
  product_id?: number | string | null;
  service_id?: number | string | null;
  unit_id?: number | string | null;
  resolved_unit_id?: number | string | null;
  unit_short_name?: string;
}

const unitId = (value: unknown): number | null =>
  Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : null;
const sameItem = (item: SalesReturnUnitItem, source: SalesReturnUnitItem) =>
  (item.item_type || "product") === (source.item_type || "product") &&
  Number(item.product_id || 0) === Number(source.product_id || 0) &&
  Number(item.service_id || 0) === Number(source.service_id || 0);

export function findSalesReturnSourceItem<T extends SalesReturnUnitItem>(item: SalesReturnUnitItem, sources: T[]): T | undefined {
  const sourceId = unitId(item.original_order_item_id);
  if (sourceId) return sources.find((source) => Number(source.id) === sourceId && sameItem(item, source));
  const candidates = sources.filter((source) => sameItem(item, source) &&
    (!unitId(item.unit_id) || !unitId(source.unit_id) || unitId(item.unit_id) === unitId(source.unit_id)));
  return candidates.length === 1 ? candidates[0] : undefined;
}

// Keep an already recorded return unit. An old missing unit inherits the sold
// line's unit (or the API's resolved base unit), never another matching product.
export function salesReturnUnitFields(item: SalesReturnUnitItem, source?: SalesReturnUnitItem) {
  const recorded = unitId(item.unit_id);
  const sold = unitId(source?.unit_id);
  return {
    unit_id: recorded || sold || unitId(source?.resolved_unit_id) || unitId(item.resolved_unit_id),
    unit_short_name: recorded ? item.unit_short_name || source?.unit_short_name
      : source?.unit_short_name || item.unit_short_name,
  };
}

export function prepareSalesReturnItemUnits<T extends SalesReturnUnitItem>(items: T[], sources: SalesReturnUnitItem[]) {
  return items.map((item) => {
    const source = findSalesReturnSourceItem(item, sources);
    if (!source) throw new Error("Une ligne du retour ne peut pas être rattachée sans ambiguïté à la vente d'origine. Rechargez le retour avant de l'enregistrer.");
    if (unitId(item.unit_id) && unitId(source.unit_id) && unitId(item.unit_id) !== unitId(source.unit_id)) {
      throw new Error("L'unité de la ligne retournée diffère de celle de la vente d'origine. Rechargez le retour avant de l'enregistrer.");
    }
    return { ...item, ...salesReturnUnitFields(item, source), original_order_item_id: Number(source.id) };
  });
}
